import React, { useState, useEffect } from 'react';
import BANKS from './bankRates';
import PageLayout from './PageLayout';
import './styles/clean.css';
import './styles/savingsInterest.css';

const CALCULATION_TYPES = { SAVINGS: 'savings', FD: 'fd' };
const SORT_TYPES = {
  NAME: 'name',
  RATE: 'rate',
  POST_TAX: 'postTax',
  SOURCE: 'source',
  CONSERVATIVE: 'conservative',
};
const CHECKED_ON = '2026-04-21';

const AMOUNT_PRESETS = [
  { label: '₹1L', value: 100000 },
  { label: '₹5L', value: 500000 },
  { label: '₹10L', value: 1000000 },
  { label: '₹25L', value: 2500000 },
  { label: '₹1Cr', value: 10000000 },
];

const TENURE_PRESETS = [
  { label: '7d', value: 7 },
  { label: '1M', value: 30 },
  { label: '3M', value: 91 },
  { label: '6M', value: 182 },
  { label: '1Y', value: 365 },
  { label: '2Y', value: 730 },
  { label: '3Y', value: 1095 },
  { label: '5Y', value: 1825 },
];

const LADDER_RUNGS = [
  { label: '1 year', days: 365 },
  { label: '2 years', days: 730 },
  { label: '3 years', days: 1095 },
];

const META_INFO = {
  title: "Bank Interest Calculator - Compare Savings & FD Rates",
  description: "Compare savings account and fixed deposit interest rates across major Indian banks.",
};

const calculateSimpleInterest = (amount, ratePA, days) => {
  const interest = amount * (ratePA / 100) * (days / 365);
  const effectiveRate = (interest / amount) * (365 / days) * 100;
  return { interest, effectiveRate, maturityAmount: amount + interest };
};

const calculateCompoundInterest = (amount, ratePA, days) => {
  const yearsFloat = days / 365;
  const periodsPerYear = 4;
  const totalPeriods = yearsFloat * periodsPerYear;
  const maturityAmount = amount * Math.pow(1 + (ratePA / (100 * periodsPerYear)), totalPeriods);
  const interest = maturityAmount - amount;
  const effectiveRate = ((maturityAmount / amount) ** (365 / days) - 1) * 100;
  return { interest, effectiveRate, maturityAmount };
};

const calculateActualReturns = (amount, ratePA, days, type) => {
  if (type === CALCULATION_TYPES.SAVINGS) {
    const interest = amount * (ratePA / 100);
    return { interest, effectiveRate: ratePA, maturityAmount: amount + interest };
  }
  if (days < 181 || days === 365) return calculateSimpleInterest(amount, ratePA, days);
  return calculateCompoundInterest(amount, ratePA, days);
};

const calculateNetMetrics = (principal, effective, taxSlab, inflationRate, days) => {
  const taxRate = Number.isFinite(taxSlab) && taxSlab > 0 ? taxSlab / 100 : 0;
  const inflation = Number.isFinite(inflationRate) && inflationRate > 0 ? inflationRate / 100 : 0;
  const years = days / 365;
  const tax = Math.max(effective.interest, 0) * taxRate;
  const postTaxInterest = effective.interest - tax;
  const postTaxMaturityAmount = principal + postTaxInterest;
  const realMaturityAmount = inflation > 0
    ? postTaxMaturityAmount / Math.pow(1 + inflation, years)
    : postTaxMaturityAmount;
  const realGain = realMaturityAmount - principal;
  const postTaxEffectiveRate = ((postTaxMaturityAmount / principal) ** (1 / years) - 1) * 100;

  return {
    tax,
    postTaxInterest,
    postTaxMaturityAmount,
    realMaturityAmount,
    realGain,
    postTaxEffectiveRate,
  };
};

const calculateBankResults = (amount, days, calculationType, taxSlab = 0, inflationRate = 0) => {
  const results = {};
  const effectiveDays = calculationType === CALCULATION_TYPES.SAVINGS ? 365 : days;
  Object.entries(BANKS).forEach(([bankName, bank]) => {
    const rawResult = calculationType === CALCULATION_TYPES.SAVINGS
      ? bank.savings(amount)
      : bank.fd(amount, days);
    if (typeof rawResult === 'string') {
      results[bankName] = rawResult;
      return;
    }
    const effective = calculateActualReturns(amount, (rawResult / amount) * 100, effectiveDays, calculationType);
    results[bankName] = {
      nominal: rawResult,
      effective,
      net: calculateNetMetrics(amount, effective, taxSlab, inflationRate, effectiveDays),
    };
  });
  return results;
};

const fmt = (n) => '₹' + Math.round(n).toLocaleString('en-IN');
const fmtFull = (n) => '₹' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const getRate = (result, calculationType, amount) => {
  if (typeof result === 'string') return -1;
  return calculationType === CALCULATION_TYPES.FD
    ? result.effective.effectiveRate
    : (result.nominal / amount * 100);
};

const getSourceConfidence = (bankName, calculationType) => {
  const source = BANKS[bankName].source;
  const description = calculationType === CALCULATION_TYPES.SAVINGS
    ? BANKS[bankName].savingsDescription
    : BANKS[bankName].fdDescription;
  if (!source) return { label: 'Needs source', score: 0, className: 'interest-source-badge--warn' };
  if (/blocked|caveat|ambig|search-index/i.test(description || '')) {
    return { label: 'Official caveat', score: 2, className: 'interest-source-badge--caution' };
  }
  const effectiveFrom = calculationType === CALCULATION_TYPES.SAVINGS
    ? source.savingsEffectiveFrom
    : source.fdEffectiveFrom;
  const sourceAgeDays = Math.floor((new Date(CHECKED_ON) - new Date(effectiveFrom)) / 86400000);
  if (sourceAgeDays > 365) {
    return { label: 'Official, older rate', score: 3, className: 'interest-source-badge--caution' };
  }
  return { label: 'Official current', score: 4, className: 'interest-source-badge--good' };
};

const getSortMetric = (bankName, result, sortBy, calculationType, amount) => {
  if (typeof result === 'string') return -Infinity;
  if (sortBy === SORT_TYPES.POST_TAX) return result.net.postTaxEffectiveRate;
  if (sortBy === SORT_TYPES.SOURCE) return getSourceConfidence(bankName, calculationType).score;
  if (sortBy === SORT_TYPES.CONSERVATIVE) {
    return getSourceConfidence(bankName, calculationType).score * 100 + getRate(result, calculationType, amount);
  }
  return getRate(result, calculationType, amount);
};

const getSortedResults = (results, sortBy, calculationType, amount) =>
  Object.entries(results).sort((a, b) => {
    if (sortBy === SORT_TYPES.NAME) return a[0].localeCompare(b[0]);
    return getSortMetric(b[0], b[1], sortBy, calculationType, amount) -
      getSortMetric(a[0], a[1], sortBy, calculationType, amount);
  });

const getRankingReason = (sortBy, bank, result, calculationType, amount) => {
  if (typeof result === 'string') return 'This result needs branch confirmation.';
  const confidence = getSourceConfidence(bank, calculationType);
  if (sortBy === SORT_TYPES.NAME) return 'Results are sorted alphabetically by bank name.';
  if (sortBy === SORT_TYPES.POST_TAX) return `${bank} has the highest estimated post-tax yield in this comparison.`;
  if (sortBy === SORT_TYPES.SOURCE) return `${bank} ranks first for source confidence: ${confidence.label.toLowerCase()}.`;
  if (sortBy === SORT_TYPES.CONSERVATIVE) {
    return `${bank} balances source confidence (${confidence.label.toLowerCase()}) with a competitive ${getRate(result, calculationType, amount).toFixed(2)}% yield.`;
  }
  return `${bank} has the highest gross ${calculationType === CALCULATION_TYPES.FD ? 'effective annual yield' : 'annual rate'} for this input.`;
};

const PinnedBankComparison = ({ pinnedResults, calculationType, calculatedAmount, taxEnabled, inflationEnabled, onClear, onRemove }) => {
  if (pinnedResults.length === 0) return null;
  return (
    <section className="interest-compare-panel" aria-label="Pinned bank comparison">
      <div className="interest-compare-head">
        <div>
          <strong>Pinned comparison</strong>
          <p>Compare up to 4 banks side by side for this amount and assumption set.</p>
        </div>
        <button type="button" className="interest-pin-btn" onClick={onClear}>Clear pins</button>
      </div>
      <div className="interest-compare-grid">
        {pinnedResults.map(([bank, result]) => {
          const sourceConfidence = getSourceConfidence(bank, calculationType);
          return (
            <article key={bank} className="interest-compare-card">
              <div className="interest-compare-card-head">
                <strong>{bank}</strong>
                <button type="button" className="interest-pin-btn" onClick={() => onRemove(bank)}>Remove</button>
              </div>
              {typeof result === 'string' ? (
                <p className="interest-compare-muted">Contact branch for this input.</p>
              ) : (
                <dl className="interest-compare-metrics">
                  <div>
                    <dt>{calculationType === CALCULATION_TYPES.FD ? 'Effective yield' : 'Annual rate'}</dt>
                    <dd>{getRate(result, calculationType, calculatedAmount).toFixed(2)}%</dd>
                  </div>
                  <div>
                    <dt>{taxEnabled ? 'Post-tax interest' : 'Interest'}</dt>
                    <dd>{fmt(taxEnabled ? result.net.postTaxInterest : result.effective.interest)}</dd>
                  </div>
                  {calculationType === CALCULATION_TYPES.FD && (
                    <div>
                      <dt>{inflationEnabled ? 'Real maturity' : taxEnabled ? 'Post-tax maturity' : 'Maturity'}</dt>
                      <dd>{fmt(inflationEnabled ? result.net.realMaturityAmount : taxEnabled ? result.net.postTaxMaturityAmount : result.effective.maturityAmount)}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Source</dt>
                    <dd>{sourceConfidence.label}</dd>
                  </div>
                </dl>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

const getBestFdForRung = (amount, days, taxSlab, inflationRate) => {
  const rungResults = calculateBankResults(amount, days, CALCULATION_TYPES.FD, taxSlab, inflationRate);
  return getSortedResults(rungResults, SORT_TYPES.POST_TAX, CALCULATION_TYPES.FD, amount)
    .find(([, result]) => typeof result !== 'string');
};

const FdLadderLite = ({ amount, taxSlab, inflationRate, taxEnabled, inflationEnabled }) => {
  if (!amount || amount <= 0) return null;
  const rungAmount = amount / LADDER_RUNGS.length;
  const rungs = LADDER_RUNGS.map(rung => {
    const best = getBestFdForRung(rungAmount, rung.days, taxSlab, inflationRate);
    return best ? { ...rung, bank: best[0], result: best[1] } : null;
  }).filter(Boolean);
  if (!rungs.length) return null;
  const totalPostTaxMaturity = rungs.reduce((sum, rung) => sum + rung.result.net.postTaxMaturityAmount, 0);
  const totalInterest = rungs.reduce((sum, rung) => sum + (taxEnabled ? rung.result.net.postTaxInterest : rung.result.effective.interest), 0);

  return (
    <section className="interest-ladder-panel" aria-label="FD ladder lite">
      <div className="interest-ladder-head">
        <div>
          <strong>FD ladder lite</strong>
          <p>Splits your principal into 1, 2, and 3-year rungs so all money does not mature on the same date.</p>
        </div>
        <span>{fmt(totalPostTaxMaturity)} est. maturity</span>
      </div>
      <div className="interest-ladder-grid">
        {rungs.map(rung => (
          <article key={rung.label} className="interest-ladder-card">
            <span>{rung.label}</span>
            <strong>{rung.bank}</strong>
            <dl>
              <div><dt>Amount</dt><dd>{fmt(rungAmount)}</dd></div>
              <div><dt>Yield</dt><dd>{rung.result.net.postTaxEffectiveRate.toFixed(2)}%</dd></div>
              <div><dt>{inflationEnabled ? 'Real maturity' : taxEnabled ? 'Post-tax maturity' : 'Maturity'}</dt><dd>{fmt(inflationEnabled ? rung.result.net.realMaturityAmount : taxEnabled ? rung.result.net.postTaxMaturityAmount : rung.result.effective.maturityAmount)}</dd></div>
            </dl>
          </article>
        ))}
      </div>
      <p className="interest-ladder-note">
        Estimated {taxEnabled ? 'post-tax ' : ''}interest across rungs: {fmt(totalInterest)}. Laddering improves liquidity timing; it does not guarantee the highest single-bank return.
      </p>
    </section>
  );
};

const TopSummary = ({ sortedResults, calculationType, calculatedAmount, sortBy, taxEnabled, inflationEnabled }) => {
  const valid = sortedResults.filter(([, r]) => typeof r !== 'string');
  if (valid.length < 1) return null;
  const [bestBank, bestResult] = valid[0];
  const bestRate = getRate(bestResult, calculationType, calculatedAmount).toFixed(2);
  const bestPostTaxRate = bestResult.net.postTaxEffectiveRate.toFixed(2);
  const avgRate = (valid.reduce((s, [, r]) => s + getRate(r, calculationType, calculatedAmount), 0) / valid.length).toFixed(2);
  const topInterest = calculationType === CALCULATION_TYPES.FD
    ? bestResult.effective.interest
    : bestResult.nominal;

  return (
    <div className="interest-summary-bar">
      <div className="interest-summary-item interest-summary-item--best">
        <span className="interest-summary-label">Best Rate</span>
        <span className="interest-summary-value">{bestRate}%</span>
        <span className="interest-summary-sub">{bestBank}</span>
      </div>
      {taxEnabled && (
        <div className="interest-summary-item">
          <span className="interest-summary-label">Post-Tax Yield</span>
          <span className="interest-summary-value">{bestPostTaxRate}%</span>
          <span className="interest-summary-sub">for {bestBank}</span>
        </div>
      )}
      <div className="interest-summary-item">
        <span className="interest-summary-label">{taxEnabled ? 'Post-Tax Return' : 'Best Return'}</span>
        <span className="interest-summary-value">{fmt(taxEnabled ? bestResult.net.postTaxInterest : topInterest)}</span>
        <span className="interest-summary-sub">
          {calculationType === CALCULATION_TYPES.FD ? 'interest earned' : 'per year'}
        </span>
      </div>
      <div className="interest-summary-item">
        <span className="interest-summary-label">Avg Rate</span>
        <span className="interest-summary-value">{avgRate}%</span>
        <span className="interest-summary-sub">across {valid.length} banks</span>
      </div>
      {calculationType === CALCULATION_TYPES.FD && (
        <div className="interest-summary-item">
          <span className="interest-summary-label">{inflationEnabled ? 'Real Maturity' : 'Best Maturity'}</span>
          <span className="interest-summary-value">
            {fmt(inflationEnabled ? bestResult.net.realMaturityAmount : bestResult.effective.maturityAmount)}
          </span>
          <span className="interest-summary-sub">at {bestBank}</span>
        </div>
      )}
      <p className="interest-ranking-reason">
        {getRankingReason(sortBy, bestBank, bestResult, calculationType, calculatedAmount)}
      </p>
    </div>
  );
};

const ResultCard = ({ bank, result, calculationType, calculatedAmount, rank, taxEnabled, inflationEnabled, isPinned, onTogglePin, canPin }) => {
  const isTop = rank === 0;
  const isSecond = rank === 1;
  const isThird = rank === 2;
  const source = BANKS[bank].source;
  const confidence = getSourceConfidence(bank, calculationType);
  const rate = typeof result !== 'string'
    ? getRate(result, calculationType, calculatedAmount).toFixed(2)
    : null;

  let rankClass = '';
  if (isTop) rankClass = 'interest-result-card--top';
  else if (isSecond) rankClass = 'interest-result-card--second';
  else if (isThird) rankClass = 'interest-result-card--third';

  return (
    <div className={`interest-result-card ${rankClass}`}>
      <div className="interest-card-header">
        <span className="interest-card-bank">{bank}</span>
        <div className="interest-card-rank-badges">
          {isTop && <span className="clean-badge clean-badge--green">🥇 Best</span>}
          {isSecond && <span className="clean-badge clean-badge--blue">🥈 2nd</span>}
          {isThird && <span className="clean-badge clean-badge--gray">🥉 3rd</span>}
          <button
            type="button"
            className={`interest-pin-btn ${isPinned ? 'interest-pin-btn--active' : ''}`}
            onClick={() => onTogglePin(bank)}
            disabled={!isPinned && !canPin}
            title={isPinned ? 'Remove from pinned comparison' : 'Pin bank to comparison'}
          >
            {isPinned ? 'Pinned' : 'Pin'}
          </button>
        </div>
      </div>
      <div className="interest-card-body">
        {typeof result === 'string' ? (
          <p style={{ fontSize: 13, color: 'var(--c-text-muted)', margin: 0 }}>Contact branch</p>
        ) : (
          <>
            <div className="interest-rate-big">{rate}%</div>
            <div className="interest-rate-label">
              {calculationType === CALCULATION_TYPES.FD ? 'Effective annual yield' : 'Annual rate'}
            </div>
            <table className="interest-detail-table">
              <tbody>
                <tr>
                  <td className="interest-detail-label">Interest</td>
                  <td className="interest-detail-value">
                    {calculationType === CALCULATION_TYPES.FD
                      ? fmtFull(result.effective.interest)
                      : fmtFull(result.nominal)}
                  </td>
                </tr>
                {taxEnabled && (
                  <>
                    <tr>
                      <td className="interest-detail-label">Est. tax</td>
                      <td className="interest-detail-value">{fmtFull(result.net.tax)}</td>
                    </tr>
                    <tr>
                      <td className="interest-detail-label">Post-tax interest</td>
                      <td className="interest-detail-value interest-detail-bold">{fmtFull(result.net.postTaxInterest)}</td>
                    </tr>
                  </>
                )}
                {calculationType === CALCULATION_TYPES.FD && (
                  <>
                    <tr>
                      <td className="interest-detail-label">Nominal rate</td>
                      <td className="interest-detail-value">{(result.nominal / calculatedAmount * 100).toFixed(2)}%</td>
                    </tr>
                    <tr>
                      <td className="interest-detail-label">{taxEnabled ? 'Post-tax maturity' : 'Maturity'}</td>
                      <td className="interest-detail-value interest-detail-bold">
                        {fmtFull(taxEnabled ? result.net.postTaxMaturityAmount : result.effective.maturityAmount)}
                      </td>
                    </tr>
                  </>
                )}
                {inflationEnabled && (
                  <>
                    <tr>
                      <td className="interest-detail-label">Real maturity</td>
                      <td className="interest-detail-value">{fmtFull(result.net.realMaturityAmount)}</td>
                    </tr>
                    <tr>
                      <td className="interest-detail-label">Real gain</td>
                      <td className="interest-detail-value">{fmtFull(result.net.realGain)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
            <div className="interest-source-row">
              <span className={`interest-source-badge ${confidence.className}`}>{confidence.label}</span>
            </div>
            <p className="interest-card-note">
              {calculationType === CALCULATION_TYPES.SAVINGS
                ? BANKS[bank].savingsDescription
                : BANKS[bank].fdDescription}
            </p>
            {source ? (
              <p className="interest-card-source">
                Verified {source.verifiedOn} · Effective from{' '}
                {calculationType === CALCULATION_TYPES.SAVINGS
                  ? source.savingsEffectiveFrom
                  : source.fdEffectiveFrom}
                .{' '}
                <a
                  href={calculationType === CALCULATION_TYPES.SAVINGS ? source.savingsUrl : source.fdUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Bank source ↗
                </a>
              </p>
            ) : (
              <p className="interest-card-source interest-card-source--unverified">
                Source revalidation pending.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const InterestCalculator = () => {
  const [inputAmount, setInputAmount] = useState('');
  const [calculatedAmount, setCalculatedAmount] = useState(null);
  const [results, setResults] = useState({});
  const [sortBy, setSortBy] = useState(SORT_TYPES.RATE);
  const [calculationType, setCalculationType] = useState(CALCULATION_TYPES.SAVINGS);
  const [tenure, setTenure] = useState('365');
  const [activeAmountPreset, setActiveAmountPreset] = useState(null);
  const [activeTenurePreset, setActiveTenurePreset] = useState('365');
  const [viewMode, setViewMode] = useState('grid'); // grid | list
  const [taxSlab, setTaxSlab] = useState('');
  const [inflationRate, setInflationRate] = useState('');
  const [pinnedBanks, setPinnedBanks] = useState([]);

  const parsedTaxSlab = parseFloat(taxSlab);
  const parsedInflationRate = parseFloat(inflationRate);
  const taxEnabled = Number.isFinite(parsedTaxSlab) && parsedTaxSlab > 0;
  const inflationEnabled = Number.isFinite(parsedInflationRate) && parsedInflationRate > 0;

  useEffect(() => {
    document.title = META_INFO.title;
    const updateMetaTag = (name, content) => {
      let tag = document.querySelector(`meta[${name}]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(name.split('=')[0], name.split('=')[1].replace(/"/g, ''));
        document.head.appendChild(tag);
      }
      tag.content = content;
    };
    updateMetaTag('name="description"', META_INFO.description);
    updateMetaTag('property="og:title"', META_INFO.title);
    updateMetaTag('property="og:description"', META_INFO.description);
    return () => { document.title = "longloop's homepage"; };
  }, []);

  const handleAmountPreset = (preset) => {
    setInputAmount(String(preset.value));
    setActiveAmountPreset(preset.value);
  };

  const handleTenurePreset = (preset) => {
    setTenure(String(preset.value));
    setActiveTenurePreset(preset.value);
  };

  const handleCalculate = () => {
    const amountValue = parseFloat(inputAmount);
    const tenureDays = parseInt(tenure);
    if (isNaN(amountValue) || amountValue <= 0) {
      alert('Please enter a valid positive number');
      return;
    }
    if (calculationType === CALCULATION_TYPES.FD && (isNaN(tenureDays) || tenureDays < 7)) {
      alert('Please enter a valid tenure (minimum 7 days)');
      return;
    }
    if (taxSlab !== '' && (isNaN(parsedTaxSlab) || parsedTaxSlab < 0 || parsedTaxSlab > 100)) {
      alert('Please enter a tax slab between 0 and 100.');
      return;
    }
    if (inflationRate !== '' && (isNaN(parsedInflationRate) || parsedInflationRate < 0 || parsedInflationRate > 100)) {
      alert('Please enter an inflation rate between 0 and 100.');
      return;
    }
    setCalculatedAmount(amountValue);
    setResults(calculateBankResults(
      amountValue,
      tenureDays,
      calculationType,
      taxEnabled ? parsedTaxSlab : 0,
      inflationEnabled ? parsedInflationRate : 0
    ));
  };

  const sortedResults = calculatedAmount
    ? getSortedResults(results, sortBy, calculationType, calculatedAmount)
    : [];
  const pinnedResults = pinnedBanks
    .map(bank => [bank, results[bank]])
    .filter(([, result]) => result !== undefined);
  const togglePinnedBank = (bank) => {
    setPinnedBanks(prev => {
      if (prev.includes(bank)) return prev.filter(item => item !== bank);
      if (prev.length >= 4) return prev;
      return [...prev, bank];
    });
  };

  return (
    <PageLayout
      title="Bank Interest Calculator"
      icon="🏦"
      subtitle="Compare savings account and fixed deposit rates across major Indian banks"
    >
      <div className="interest-page">
        {/* Form card */}
        <div className="interest-form">
          {/* Type selector */}
          <div className="interest-type-selector">
            {Object.values(CALCULATION_TYPES).map(type => (
              <button
                key={type}
                className={`interest-type-btn ${calculationType === type ? 'interest-type-btn--active' : ''}`}
                onClick={() => setCalculationType(type)}
                title={type === CALCULATION_TYPES.SAVINGS ? 'Use this when money stays in a savings account for a year.' : 'Use this when money is locked in a fixed deposit for a chosen tenure.'}
              >
                {type === CALCULATION_TYPES.SAVINGS ? '💰 Savings Account' : '🔒 Fixed Deposit'}
              </button>
            ))}
          </div>

          {/* Amount input */}
          <div className="interest-input-group">
            <label className="clean-label" htmlFor="interest-amount">
              {calculationType === CALCULATION_TYPES.SAVINGS ? 'Average Quarterly Balance' : 'Principal Amount'}
            </label>
            <div className="interest-amount-row">
              <div className="interest-input-prefix-wrap">
                <span className="interest-input-prefix">₹</span>
                <input
                  id="interest-amount"
                  type="number"
                  value={inputAmount}
                  onChange={(e) => { setInputAmount(e.target.value); setActiveAmountPreset(null); }}
                  placeholder="e.g. 100000"
                  className="clean-input interest-input-with-prefix"
                  onKeyDown={(e) => e.key === 'Enter' && handleCalculate()}
                />
              </div>
            </div>
            <div className="interest-presets">
              {AMOUNT_PRESETS.map(p => (
                <button
                  key={p.value}
                  className={`interest-preset-btn ${activeAmountPreset === p.value ? 'interest-preset-btn--active' : ''}`}
                  onClick={() => handleAmountPreset(p)}
                  title={`Set amount to ${p.label}.`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tenure input (FD only) */}
          {calculationType === CALCULATION_TYPES.FD && (
            <div className="interest-input-group">
              <label className="clean-label" htmlFor="interest-tenure">Tenure (days)</label>
              <div className="interest-amount-row">
                <input
                  id="interest-tenure"
                  type="number"
                  value={tenure}
                  onChange={(e) => { setTenure(e.target.value); setActiveTenurePreset(null); }}
                  placeholder="e.g. 365"
                  min="7"
                  className="clean-input"
                  style={{ maxWidth: 160 }}
                  onKeyDown={(e) => e.key === 'Enter' && handleCalculate()}
                />
                <span style={{ fontSize: 13, color: 'var(--c-text-muted)', alignSelf: 'center' }}>days</span>
              </div>
              <div className="interest-presets">
                {TENURE_PRESETS.map(p => (
                  <button
                    key={p.value}
                    className={`interest-preset-btn ${activeTenurePreset === String(p.value) || activeTenurePreset === p.value ? 'interest-preset-btn--active' : ''}`}
                    onClick={() => handleTenurePreset(p)}
                    title={`Set FD tenure to ${p.label}.`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="interest-advanced-grid">
            <div className="interest-input-group">
              <label className="clean-label" htmlFor="interest-tax-slab">Tax slab % (optional)</label>
              <input
                id="interest-tax-slab"
                type="number"
                value={taxSlab}
                onChange={(e) => setTaxSlab(e.target.value)}
                placeholder="e.g. 30"
                min="0"
                max="100"
                className="clean-input"
                onKeyDown={(e) => e.key === 'Enter' && handleCalculate()}
              />
              <span className="interest-helper">Shows estimated post-tax return.</span>
            </div>
            <div className="interest-input-group">
              <label className="clean-label" htmlFor="interest-inflation">Inflation % (optional)</label>
              <input
                id="interest-inflation"
                type="number"
                value={inflationRate}
                onChange={(e) => setInflationRate(e.target.value)}
                placeholder="e.g. 6"
                min="0"
                max="100"
                className="clean-input"
                onKeyDown={(e) => e.key === 'Enter' && handleCalculate()}
              />
              <span className="interest-helper">Shows purchasing-power adjusted maturity.</span>
            </div>
          </div>

          {/* Actions row */}
          <div className="interest-controls-row">
            <button className="clean-btn clean-btn--primary" onClick={handleCalculate} title="Calculate returns for all banks using the amount, tenure, tax, and inflation assumptions above.">
              Compare All Banks →
            </button>
            {calculatedAmount !== null && (
              <div className="interest-controls-right">
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="clean-select" title="Choose which result should rank first.">
                  <option value={SORT_TYPES.RATE}>Highest gross return</option>
                  <option value={SORT_TYPES.POST_TAX}>Highest post-tax return</option>
                  <option value={SORT_TYPES.SOURCE}>Newest source first</option>
                  <option value={SORT_TYPES.CONSERVATIVE}>Conservative/source-first</option>
                  <option value={SORT_TYPES.NAME}>A–Z by bank</option>
                </select>
                <div className="interest-view-toggle">
                  <button
                    className={`interest-view-btn ${viewMode === 'grid' ? 'interest-view-btn--active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                  >⊞</button>
                  <button
                    className={`interest-view-btn ${viewMode === 'list' ? 'interest-view-btn--active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List view"
                  >☰</button>
                </div>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                  {sortedResults.length} banks
                </span>
              </div>
            )}
          </div>

          <details className="interest-help-details">
            <summary>What do these controls mean?</summary>
            <dl>
              <div><dt>Amount</dt><dd>Savings mode uses average quarterly balance; FD mode uses principal invested.</dd></div>
              <div><dt>Tax slab</dt><dd>Estimates tax on interest so banks can be compared after tax.</dd></div>
              <div><dt>Inflation</dt><dd>Converts maturity into purchasing-power adjusted value.</dd></div>
              <div><dt>Pin</dt><dd>Keeps selected banks in a small comparison tray while you scan other results.</dd></div>
            </dl>
          </details>
        </div>

        {/* Source note */}
        <p className="clean-note">
          Rates are source-backed from official bank pages/PDFs where available, reviewed on 2026-04-21.
          BOI required search-index extraction (official pages block direct scraping). Always verify with your bank before investing.
        </p>

        {/* Top summary bar */}
        {calculatedAmount !== null && (
          <TopSummary
            sortedResults={sortedResults}
            calculationType={calculationType}
            calculatedAmount={calculatedAmount}
            sortBy={sortBy}
            taxEnabled={taxEnabled}
            inflationEnabled={inflationEnabled}
          />
        )}

        {calculatedAmount !== null && (taxEnabled || inflationEnabled) && (
          <p className="clean-note clean-note--info">
            Estimates use {taxEnabled ? `${parsedTaxSlab}% tax slab` : 'no tax adjustment'}
            {inflationEnabled ? ` and ${parsedInflationRate}% inflation` : ''}. Actual tax, TDS, compounding, and bank rules can differ.
          </p>
        )}

        {calculatedAmount !== null && (
          <PinnedBankComparison
            pinnedResults={pinnedResults}
            calculationType={calculationType}
            calculatedAmount={calculatedAmount}
            taxEnabled={taxEnabled}
            inflationEnabled={inflationEnabled}
            onClear={() => setPinnedBanks([])}
            onRemove={(bank) => setPinnedBanks(prev => prev.filter(item => item !== bank))}
          />
        )}

        {calculatedAmount !== null && calculationType === CALCULATION_TYPES.FD && (
          <FdLadderLite
            amount={calculatedAmount}
            taxSlab={taxEnabled ? parsedTaxSlab : 0}
            inflationRate={inflationEnabled ? parsedInflationRate : 0}
            taxEnabled={taxEnabled}
            inflationEnabled={inflationEnabled}
          />
        )}

        {/* Results */}
        {calculatedAmount !== null && (
          <div className={viewMode === 'grid' ? 'interest-results-grid' : 'interest-results-list'}>
            {sortedResults.map(([bank, result], idx) => (
              <ResultCard
                key={bank}
                bank={bank}
                result={result}
                calculationType={calculationType}
                calculatedAmount={calculatedAmount}
                rank={idx}
                taxEnabled={taxEnabled}
                inflationEnabled={inflationEnabled}
                isPinned={pinnedBanks.includes(bank)}
                onTogglePin={togglePinnedBank}
                canPin={pinnedBanks.length < 4}
              />
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default InterestCalculator;
